'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import Cookies from 'js-cookie';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import styles from './EditableSliceWrapper.module.scss';
import { Dropzone } from '../EditorText/Dropzone';
import { LinkFieldInput } from './LinkFieldInput';
import { parseJwt } from '@/src/helpers/decode-jwt';
import { addDeploymentToast } from '@/src/helpers/add-deployment-tip';
import { authValidation } from '@/src/helpers/authValidation';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { AddSliceModal } from './AddSlice/AddSliceModal';
import { sliceDefinitions } from '@/src/types/sliceDefinitions';

type EditableSliceWrapperProps = {
  children: React.ReactNode;
  slice: Record<string, any>;
  sliceIndex: number;
  lang: 'fr';
  pageKey: string;
};

type SchemaFieldDef = {
  key: string;
  label?: string;
  type?: string;
  default?: any;
};

export const EditableSliceWrapper = ({
  children,
  slice,
  sliceIndex,
  lang,
  pageKey,
}: EditableSliceWrapperProps) => {
  const [opened, setOpened] = useState(false);
  const [addModalOpened, setAddModalOpened] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [disconnected, setDisconnected] = useState(false);
  const [validated, setValidated] = useState(false);

  const router = useRouter();
  useDictionary();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch('/api/validate-token', {
          method: 'GET',
          credentials: 'include',
        });

        if (!res.ok) {
          setIsAdmin(false);
          setIsSuperAdmin(false);
          return;
        }

        const data = await res.json();

        if (data?.valid && data?.user?.role === 'superAdmin') {
          setIsSuperAdmin(true);
          setIsAdmin(false);
        } else if (data?.valid && data?.user?.role === 'admin') {
          setIsAdmin(true);
          setIsSuperAdmin(false);
        } else {
          setIsAdmin(false);
          setIsSuperAdmin(false);
        }
      } catch (error) {
        console.error('Auth validation failed', error);
        setIsAdmin(false);
        setIsSuperAdmin(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const defaults = getSchemaDefaults(slice?.type);
    setFormValues(deepMerge(structuredClone(defaults), structuredClone(slice)));
  }, [slice]);

  const hasEditableContent = useMemo(() => {
    return !!formValues && typeof formValues === 'object' && Object.keys(formValues).length > 0;
  }, [formValues]);

  const handleSave = async () => {
    const response = await fetch('/api/slices', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lang,
        pageKey,
        sliceIndex,
        updates: formValues,
      }),
    });

    if (response.ok) {
      addDeploymentToast('Déploiement des slices en cours. Optimisation des fichiers...');
      setOpened(false);
    } else {
      alert('Erreur lors de la sauvegarde du bloc');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Supprimer ce bloc ?')) return;

    const res = await fetch('/api/slices/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pageKey,
        sliceIndex,
        locale: lang,
      }),
    });

    if (res.ok) {
      addDeploymentToast('Suppression des slices en cours. Optimisation des fichiers...');
      setOpened(false);
    } else {
      alert('Erreur lors de la suppression');
    }
  };

  const handleAdd = async (newSlice: any) => {
    const res = await fetch('/api/slices/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pageKey,
        newSlice,
        locale: lang,
        position: sliceIndex + 1,
      }),
    });

    if (res.ok) {
      addDeploymentToast('Déploiement des nouvelles slices en cours. Optimisation des fichiers...');
      setAddModalOpened(false);
    } else {
      alert('Erreur lors de l’ajout du bloc');
    }
  };

  const handleChange = (path: string, value: any) => {
    setFormValues((prev) => {
      const updated = structuredClone(prev);
      setDeepValue(updated, path, value);
      return updated;
    });
  };

  const handleRemoveArrayItem = (path: string, index: number) => {
    setFormValues((prev) => {
      const updated = structuredClone(prev);
      const arr = getDeepValue(updated, path);
      if (!Array.isArray(arr)) return prev;
      arr.splice(index, 1);
      setDeepValue(updated, path, arr);
      return updated;
    });
  };

  const handleAddArrayItem = (path: string) => {
    setFormValues((prev) => {
      const updated = structuredClone(prev);
      const arr = getDeepValue(updated, path);

      if (!Array.isArray(arr)) return prev;

      const nextItem = createNewArrayItem(path, arr, slice?.type);
      arr.push(nextItem);
      setDeepValue(updated, path, arr);

      return updated;
    });
  };

  const handleOpen = async (value: boolean) => {
    const isValid = await authValidation();
    if (!isValid) {
      setDisconnected(true);
      return;
    }
    setValidated(true);
    setOpened(value);
  };

  const handleDeleteClick = async () => {
    const isValid = await authValidation();
    if (!isValid) {
      setDisconnected(true);
      return;
    }
    setValidated(true);
    handleDelete();
  };

  const handleOpenClick = async (value: boolean) => {
    const isValid = await authValidation();
    if (!isValid) {
      setDisconnected(true);
      return;
    }
    setValidated(true);
    setAddModalOpened(value);
  };

  const renderField = (
    value: any,
    path = '',
    label?: string,
    parentKey?: string,
  ): React.ReactNode => {
    const currentKey = path.split('.').pop()?.replace(/\[\d+\]/g, '') || parentKey || '';
    const displayLabel = label || prettifyLabel(currentKey || 'field');

    if (Array.isArray(value)) {
      return renderArrayField(value, path, displayLabel);
    }

    if (typeof value === 'boolean') {
      return (
        <Checkbox
          key={path}
          label={displayLabel}
          checked={value}
          onChange={(e) => handleChange(path, e.currentTarget.checked)}
        />
      );
    }

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      value === null ||
      value === undefined
    ) {
      return renderPrimitiveField(value, path, displayLabel, currentKey);
    }

    if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value).filter(([key]) => key !== '__typename');

      if (entries.length === 0) return null;

      return (
        <div
          key={path || 'root-object'}
          style={{
            padding: path ? '0.9rem' : 0,
            border: path ? '1px solid rgba(0,0,0,0.08)' : 'none',
            borderRadius: path ? 12 : 0,
            background: path ? 'rgba(0,0,0,0.015)' : 'transparent',
          }}
        >
          {path && (
            <Text fw={700} mb="sm">
              {displayLabel}
            </Text>
          )}

          <Stack gap="sm">
            {entries.map(([key, childValue]) => {
              const childPath = path ? `${path}.${key}` : key;
              return renderField(childValue, childPath, prettifyLabel(key), key);
            })}
          </Stack>
        </div>
      );
    }

    return null;
  };

  const renderPrimitiveField = (
    rawValue: string | number | null | undefined,
    path: string,
    label: string,
    keyName: string,
  ) => {
    const value = rawValue ?? '';
    const stringValue = String(value);
    const lowerKey = keyName.toLowerCase();

    const schemaField = findSchemaField(slice?.type, path);
    const schemaType = schemaField?.type?.toLowerCase();

    const isImage =
      schemaType === 'image' ||
      isImageFieldKey(lowerKey) ||
      (looksLikeImageUrl(stringValue) && !isProbablyLinkField(lowerKey));

    const isLink = schemaType === 'link' || isProbablyLinkField(lowerKey);

    const isLongText =
      schemaType === 'textarea' ||
      lowerKey.includes('description') ||
      lowerKey.includes('text') ||
      lowerKey.includes('paragraph') ||
      lowerKey.includes('subtitle') ||
      stringValue.length > 120;

    const isVersionSwitch = lowerKey === 'version';

    if (isVersionSwitch) {
      return (
        <Switch
          key={path}
          label="Thème sombre"
          checked={stringValue === 'dark'}
          onChange={(e) => handleChange(path, e.currentTarget.checked ? 'dark' : 'light')}
        />
      );
    }

    if (isImage) {
      return (
        <div key={path}>
          <Text fw={700} mb={4}>
            {label}
          </Text>
          <Dropzone
            imageUrl={getCleanUrl(stringValue)}
            // @ts-ignore
            setImageUrl={(url: string) => handleChange(path, url)}
          />
        </div>
      );
    }

    if (isLink) {
      return (
        <div key={path}>
          <Text fw={700} mb={4}>
            {label}
          </Text>
          <LinkFieldInput value={stringValue} onChange={(val) => handleChange(path, val)} lang={lang} />
        </div>
      );
    }

    if (isLongText) {
      return (
        <div key={path}>
          <Text fw={700} mb={4}>
            {label}
          </Text>
          <Textarea
            value={stringValue}
            onChange={(e) => handleChange(path, e.currentTarget.value)}
            autosize
            minRows={3}
          />
        </div>
      );
    }

    return (
      <div key={path}>
        <Text fw={700} mb={4}>
          {label}
        </Text>
        <TextInput value={stringValue} onChange={(e) => handleChange(path, e.currentTarget.value)} />
      </div>
    );
  };

  const renderArrayField = (value: any[], path: string, label: string) => {
    const isPrimitiveArray = value.every(
      (item) =>
        item === null ||
        item === undefined ||
        typeof item === 'string' ||
        typeof item === 'number' ||
        typeof item === 'boolean',
    );

    return (
      <div
        key={path}
        style={{
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 12,
          padding: '0.9rem',
          background: 'rgba(0,0,0,0.015)',
        }}
      >
        <Group justify="space-between" mb="sm">
          <Text fw={700}>{label}</Text>

          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => handleAddArrayItem(path)}
          >
            Ajouter un élément
          </Button>
        </Group>

        {value.length === 0 ? (
          <Text size="sm" c="dimmed">
            Aucun élément.
          </Text>
        ) : isPrimitiveArray ? (
          <Stack gap="sm">
            {value.map((item, index) => {
              const itemPath = `${path}[${index}]`;

              return (
                <Group key={itemPath} align="flex-start" wrap="nowrap">
                  <div style={{ flex: 1 }}>
                    {renderPrimitiveField(
                      item,
                      itemPath,
                      `${label} ${index + 1}`,
                      path.split('.').pop() || '',
                    )}
                  </div>

                  <ActionIcon
                    color="red"
                    variant="light"
                    mt={28}
                    onClick={() => handleRemoveArrayItem(path, index)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              );
            })}
          </Stack>
        ) : (
          <Accordion variant="separated" radius="md">
            {value.map((item, index) => {
              const itemPath = `${path}[${index}]`;
              const itemLabel = getAccordionItemLabel(item, index, label);

              return (
                <Accordion.Item key={itemPath} value={itemPath}>
                  <Accordion.Control>
                    <Group justify="space-between" wrap="nowrap">
                      <Text fw={600}>{itemLabel}</Text>
                    </Group>
                  </Accordion.Control>

                  <Accordion.Panel>
                    <Stack gap="sm">
                      {typeof item === 'object' && item !== null ? (
                        Object.entries(item).map(([childKey, childValue]) =>
                          renderField(
                            childValue,
                            `${itemPath}.${childKey}`,
                            prettifyLabel(childKey),
                            childKey,
                          ),
                        )
                      ) : (
                        renderPrimitiveField(item, itemPath, `${label} ${index + 1}`, label)
                      )}

                      <Group justify="flex-end">
                        <Button
                          color="red"
                          variant="light"
                          leftSection={<IconTrash size={14} />}
                          onClick={() => handleRemoveArrayItem(path, index)}
                        >
                          Supprimer cet élément
                        </Button>
                      </Group>
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </div>
    );
  };

  return (
    <div className={styles.EditableSliceWrapper}>
      {(isSuperAdmin || isAdmin) && (
        <div className={styles.buttonsUpdate}>
          {hasEditableContent && (
            <Button size="xs" color="black" onClick={() => handleOpen(true)}>
              ✏️
            </Button>
          )}

          <Button size="xs" color="red" onClick={handleDeleteClick}>
            🗑️
          </Button>
        </div>
      )}

      {children}

      {isSuperAdmin && (
        <>
          <div
            onClick={() => handleOpenClick(true)}
            style={{
              cursor: 'pointer',
              textAlign: 'center',
              padding: '1rem 0',
              color: '#aaa',
              userSelect: 'none',
            }}
          >
            <Divider label="+ Choisir l'élement à ajouter" labelPosition="center" />
          </div>

          <AddSliceModal
            opened={addModalOpened}
            onClose={() => setAddModalOpened(false)}
            onAdd={handleAdd}
          />
        </>
      )}

      <Modal
        opened={disconnected}
        onClose={async () => {
          await fetch('/api/logout', {
            method: 'POST',
            credentials: 'include',
          });
          router.push('/login');
          router.refresh();
        }}
        title="Session Expired"
        centered
      >
        <p>Your session has expired. Please log in again.</p>
        <Button component={Link} href="/login">
          Go to Login
        </Button>
      </Modal>

      <Modal
        opened={opened && !disconnected}
        onClose={() => setOpened(false)}
        title="Modifier ce bloc"
        size="xl"
        centered
      >
        <Stack gap="md">
          {validated && hasEditableContent ? (
            renderField(formValues)
          ) : (
            <Text size="sm" c="dimmed">
              Aucun champ éditable.
            </Text>
          )}

          <Button fullWidth mt="md" onClick={handleSave}>
            Enregistrer
          </Button>
        </Stack>
      </Modal>
    </div>
  );
};

/* =========================
   Helpers
   ========================= */

function getSchemaDefaults(type?: string) {
  if (!type) return {};
  const def: any = (sliceDefinitions as any)[type];
  if (!def?.fields) return {};

  const acc: Record<string, any> = {};

  for (const f of def.fields) {
    if (Object.prototype.hasOwnProperty.call(f, 'default')) {
      setDeepValue(acc, f.key, structuredClone(f.default));
    } else if (f.type === 'boolean') {
      setDeepValue(acc, f.key, false);
    }
  }

  if (def?.arrayField?.default !== undefined) {
    setDeepValue(acc, def.arrayField.key, structuredClone(def.arrayField.default));
  }

  return acc;
}

function findSchemaField(type: string | undefined, path: string): SchemaFieldDef | undefined {
  if (!type) return undefined;
  const def: any = (sliceDefinitions as any)[type];
  if (!def?.fields?.length) return undefined;

  const normalizedPath = normalizeSchemaPath(path);

  return def.fields.find((field: SchemaFieldDef) => {
    return normalizeSchemaPath(field.key) === normalizedPath;
  });
}

function normalizeSchemaPath(path: string): string {
  return path.replace(/\[\d+\]/g, '[]');
}

function isImageFieldKey(key: string): boolean {
  const normalized = key.toLowerCase();

  return (
    normalized === 'src' ||
    normalized === 'image' ||
    normalized === 'imageurl' ||
    normalized === 'backgroundimageurl' ||
    normalized === 'image_url' ||
    normalized === 'url' ||
    normalized.endsWith('src') ||
    normalized.endsWith('image') ||
    normalized.endsWith('imageurl') ||
    normalized.endsWith('backgroundimageurl')
  );
}

function isProbablyLinkField(key: string): boolean {
  const normalized = key.toLowerCase();

  return (
    normalized === 'href' ||
    normalized === 'link' ||
    normalized === 'buttonurl' ||
    normalized === 'ctaurl' ||
    normalized.endsWith('href') ||
    normalized.endsWith('link') ||
    normalized.endsWith('url') ||
    normalized.includes('buttonurl') ||
    normalized.includes('cta')
  );
}

function looksLikeImageUrl(value: string): boolean {
  const v = getCleanUrl(value).toLowerCase();

  return (
    v.startsWith('http://') ||
    v.startsWith('https://') ||
    v.startsWith('/') ||
    /\.(jpg|jpeg|png|webp|gif|avif|svg)(\?|$)/i.test(v) ||
    v.includes('imagekit') ||
    v.includes('cloudinary') ||
    v.includes('/storage/v1/object/')
  );
}

function prettifyLabel(value: string): string {
  return value
    .replace(/\[\d+\]/g, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (s) => s.toUpperCase());
}

function getAccordionItemLabel(item: any, index: number, fallback: string): string {
  if (item && typeof item === 'object') {
    if (typeof item.imageTitle === 'string' && item.imageTitle.trim()) return item.imageTitle;
    if (typeof item.title === 'string' && item.title.trim()) return item.title;
    if (typeof item.label === 'string' && item.label.trim()) return item.label;
    if (typeof item.alt === 'string' && item.alt.trim()) return item.alt;
    if (typeof item.id === 'string' && item.id.trim()) return `${fallback} - ${item.id}`;
  }

  return `${fallback} ${index + 1}`;
}

function createNewArrayItem(path: string, currentArray: any[], type?: string): any {
  const existingFirst = currentArray[0];

  if (existingFirst !== undefined) {
    return buildEmptyFromShape(existingFirst);
  }

  const def: any = type ? (sliceDefinitions as any)[type] : undefined;

  if (def?.arrayField?.key && normalizeSchemaPath(def.arrayField.key) === normalizeSchemaPath(path)) {
    if (def.arrayField.default !== undefined) {
      return structuredClone(def.arrayField.default);
    }
  }

  return '';
}

function buildEmptyFromShape(template: any): any {
  if (Array.isArray(template)) return [];
  if (typeof template === 'boolean') return false;
  if (typeof template === 'number') return 0;
  if (typeof template === 'string') return '';
  if (template && typeof template === 'object') {
    const obj: Record<string, any> = {};
    Object.entries(template).forEach(([key, value]) => {
      obj[key] = buildEmptyFromShape(value);
    });
    return obj;
  }
  return '';
}

function getCleanUrl(value: string): string {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const url = new URL(value, base || 'http://local');
    if (url.pathname === '/api/image-proxy' && url.searchParams.has('url')) {
      return decodeURIComponent(url.searchParams.get('url') || '');
    }
  } catch {
    //
  }
  return value;
}

function setDeepValue(obj: any, path: string, value: any): void {
  const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const nextKey = keys[i + 1];
    const shouldBeArray = /^\d+$/.test(nextKey);

    if (!(k in current) || current[k] == null) {
      current[k] = shouldBeArray ? [] : {};
    }

    current = current[k];
  }

  current[keys[keys.length - 1]] = value;
}

function getDeepValue(obj: any, path: string): any {
  const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
  return keys.reduce((acc, key) => acc?.[key], obj);
}

function deepMerge(target: any, source: any): any {
  if (Array.isArray(target) && Array.isArray(source)) {
    return source;
  }

  if (
    target &&
    source &&
    typeof target === 'object' &&
    typeof source === 'object' &&
    !Array.isArray(target) &&
    !Array.isArray(source)
  ) {
    const result = { ...target };

    Object.keys(source).forEach((key) => {
      result[key] = key in target ? deepMerge(target[key], source[key]) : source[key];
    });

    return result;
  }

  return source;
}